// Global auto-popup for the GTM Sales edit-access approval workflow (iter246).
// Behaviour
// - Runs only for Admin + gtm_sales_leader roles.
// - On mount and every 30s afterwards, polls /api/edit-access/pending.
// - When at least one PENDING request is found, opens a small popup that
//   lists the requests, letting the reviewer open the account (which
//   triggers the LeadDetails decision popup) or dismiss the toast without
//   deciding. Dismissing an item locally hides it for this session; the
//   request remains pending server-side.
// - Never renders for non-approver roles.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, X, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const POLL_MS = 30000;
const DISMISS_KEY = 'crm_edit_access_dismissed_v1';

const readDismissed = () => {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(DISMISS_KEY) || '[]'));
  } catch (_) {
    return new Set();
  }
};

const writeDismissed = (set) => {
  try { sessionStorage.setItem(DISMISS_KEY, JSON.stringify(Array.from(set))); } catch (_) { /* silent */ }
};

const EditAccessAutoPopup = () => {
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [dismissed, setDismissed] = useState(readDismissed());
  const timerRef = useRef(null);

  const role = (user?.role || '').toLowerCase();
  const isApprover = role === 'admin' || role === 'gtm_sales_leader';

  const fetchPending = useCallback(async () => {
    if (!isApprover || !token) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/edit-access/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data?.data) ? data.data : [];
      setRequests(list);
    } catch (_) { /* silent */ }
  }, [isApprover, token]);

  useEffect(() => {
    if (!isAuthenticated || !isApprover) {
      setRequests([]);
      return undefined;
    }
    fetchPending();
    timerRef.current = setInterval(fetchPending, POLL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isAuthenticated, isApprover, fetchPending]);

  if (!isApprover) return null;
  const visible = requests.filter(r => !dismissed.has(r.id));
  if (visible.length === 0) return null;

  const dismissOne = (id) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    writeDismissed(next);
  };

  const openLead = (req) => {
    navigate(`/leads/${req.lead_id}?edit_access_req=${encodeURIComponent(req.id)}`);
  };

  return (
    <div className="fixed bottom-6 right-6 z-40 w-[360px] space-y-2" data-testid="edit-access-auto-popup">
      {visible.slice(0, 3).map(req => (
        <div key={req.id} className="bg-white rounded-xl shadow-2xl border border-amber-200 overflow-hidden" data-testid={`edit-access-toast-${req.id}`}>
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-amber-50">
            <div className="flex items-center gap-2">
              <AlertCircle size={16} className="text-amber-600" />
              <p className="text-sm font-semibold text-gray-800">Edit Access Request</p>
            </div>
            <button
              type="button"
              onClick={() => dismissOne(req.id)}
              className="text-gray-400 hover:text-gray-600"
              title="Dismiss (does not approve or reject)"
              data-testid={`edit-access-toast-dismiss-${req.id}`}
            >
              <X size={14} />
            </button>
          </div>
          <div className="p-4">
            <p className="text-sm text-gray-700">
              <strong>{req.requester_name}</strong> needs access to edit
              <strong> {req.lead_name}</strong>.
            </p>
            <button
              type="button"
              onClick={() => openLead(req)}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-teal-700 hover:text-teal-800"
              data-testid={`edit-access-toast-open-${req.id}`}
            >
              Open account <ArrowRight size={12} />
            </button>
          </div>
        </div>
      ))}
      {visible.length > 3 && (
        <p className="text-xs text-gray-500 bg-white/80 rounded px-2 py-1 border border-gray-200">
          +{visible.length - 3} more pending requests
        </p>
      )}
    </div>
  );
};

export default EditAccessAutoPopup;
